import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Target, Users, MessageSquare, Mail, Zap, Loader2, Plus, Send, TrendingUp, BarChart3, PieChart } from "lucide-react";
import { toast } from "sonner";

export default function AIMarketingPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(null);
  const [segments, setSegments] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: "", segment: "inactive_15d", message_template: "", discount_percentage: 10 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setPolling(true);
    try {
      const [cfgRes, segRes, campRes, statsRes] = await Promise.all([
        authAxiosRef.current.get("/ai-marketing/config"),
        authAxiosRef.current.get("/ai-marketing/segments"),
        authAxiosRef.current.get("/ai-marketing/campaigns"),
        authAxiosRef.current.get("/ai-marketing/stats"),
      ]);
      setConfig(cfgRes.data);
      setSegments(segRes.data);
      setCampaigns(campRes.data.campaigns || []);
      setStats(statsRes.data);
    } catch { if (!silent) toast.error("Erro ao carregar dados"); }
    setLoading(false);
    setPolling(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll while any campaign is sending — silent refresh every 15s, no spinner
  useEffect(() => {
    const hasSending = campaigns.some(c => c.status === "sending");
    if (!hasSending) return;
    const t = setInterval(() => fetchData(true), 15000);
    return () => clearInterval(t);
  }, [campaigns, fetchData]);

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      const res = await authAxios.put("/ai-marketing/config", { ...config, enabled });
      setConfig(res.data);
      toast.success(enabled ? "Marketing IA ativado!" : "Marketing IA desativado");
    } catch { toast.error("Erro ao atualizar"); }
    setSaving(false);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put("/ai-marketing/config", config);
      setConfig(res.data);
      toast.success("Configuracoes salvas!");
    } catch { toast.error("Erro"); }
    setSaving(false);
  };

  const handleCreateCampaign = async () => {
    try {
      await authAxios.post("/ai-marketing/campaigns", newCampaign);
      toast.success("Campanha criada!");
      setShowNewCampaign(false);
      setNewCampaign({ name: "", segment: "inactive_15d", message_template: "", discount_percentage: 10 });
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Erro ao criar campanha");
    }
  };

  const handleResendCampaign = async (campaignId) => {
    try {
      await authAxios.post(`/ai-marketing/campaigns/${campaignId}/send`);
      toast.success("Reenvio iniciado!");
      fetchData();
    } catch { toast.error("Erro ao reenviar"); }
  };

  if (loading) return <MainLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div></MainLayout>;

  const segColors = { emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", orange: "bg-orange-100 text-orange-700", red: "bg-red-100 text-red-700", gray: "bg-gray-100 text-gray-700", purple: "bg-purple-100 text-purple-700", teal: "bg-teal-100 text-teal-700", cyan: "bg-cyan-100 text-cyan-700", blue: "bg-blue-100 text-blue-700" };

  return (
    <MainLayout>
    <div className="space-y-6 max-w-5xl" data-testid="ai-marketing-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><Target className="w-5 h-5 text-emerald-600" /> Marketing Automatico com IA</h2>
          <p className="text-sm text-muted-foreground mt-1">Reative clientes inativos automaticamente com mensagens personalizadas e cupons.</p>
        </div>
        <Switch checked={config?.enabled || false} onCheckedChange={handleToggle} disabled={saving} data-testid="marketing-toggle" />
      </div>

      {/* WhatsApp status warning */}
      {stats && !stats.wa_connected && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <Zap className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <span>WhatsApp não conectado. As campanhas serão enviadas por <strong>email</strong> como fallback. <a href="/chatbot" className="underline font-medium">Conectar WhatsApp</a></span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-muted-foreground">Campanhas</p><p className="text-2xl font-black">{stats.total_campaigns}</p></div>
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-muted-foreground">Mensagens enviadas</p><p className="text-2xl font-black">{stats.total_messages_sent}</p></div>
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-muted-foreground">Conversoes</p><p className="text-2xl font-black text-emerald-600">{stats.total_conversions}</p></div>
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-muted-foreground">Taxa conversao</p><p className="text-2xl font-black text-blue-600">{stats.conversion_rate}%</p></div>
        </div>
      )}

      {/* Customer Segments */}
      {segments && (
        <div className="bg-white border rounded-xl p-5">
          <h3 className="font-bold flex items-center gap-2 mb-4"><Users className="w-4 h-4" /> Segmentos de Clientes ({segments.total_customers} total)</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {segments.segments.map(seg => (
              <div key={seg.id} className="flex items-center justify-between rounded-xl border p-3" data-testid={`segment-${seg.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${segColors[seg.color] || "bg-gray-100"}`}>{seg.count}</span>
                  <span className="text-sm">{seg.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-Reactivation Config */}
      {config?.enabled && (
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Reativacao Automatica</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { key: "reactivation_7d", label: "7 dias sem comprar" },
              { key: "reactivation_15d", label: "15 dias sem comprar" },
              { key: "reactivation_30d", label: "30 dias sem comprar" },
              { key: "reactivation_60d", label: "60 dias sem comprar" },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm">{item.label}</span>
                <Switch checked={config[item.key] || false} onCheckedChange={v => setConfig(prev => ({ ...prev, [item.key]: v }))} data-testid={`toggle-${item.key}`} />
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Desconto automatico (%)</label>
              <Input type="number" value={config.discount_percentage || 10} onChange={e => setConfig(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))} data-testid="discount-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Canal</label>
              <Select value={config.channel || "whatsapp"} onValueChange={v => setConfig(prev => ({ ...prev, channel: v }))}>
                <SelectTrigger data-testid="channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="both">Ambos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleSaveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-marketing-config">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Salvar configuracoes
          </Button>
        </div>
      )}

      {/* Campaigns */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            <Send className="w-4 h-4" /> Campanhas
            {polling && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          </h3>
          <Button size="sm" onClick={() => setShowNewCampaign(!showNewCampaign)} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="new-campaign-btn">
            <Plus className="w-3 h-3" /> Nova campanha
          </Button>
        </div>

        {showNewCampaign && (
          <div className="border rounded-xl p-4 mb-4 space-y-3 bg-emerald-50" data-testid="new-campaign-form">
            <Input placeholder="Nome da campanha" value={newCampaign.name} onChange={e => setNewCampaign(p => ({ ...p, name: e.target.value }))} />
            <Select value={newCampaign.segment} onValueChange={v => setNewCampaign(p => ({ ...p, segment: v }))}>
              <SelectTrigger><SelectValue placeholder="Segmento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inactive_7d">Inativos 7-15 dias</SelectItem>
                <SelectItem value="inactive_15d">Inativos 15-30 dias</SelectItem>
                <SelectItem value="inactive_30d">Inativos 30-60 dias</SelectItem>
                <SelectItem value="inactive_60d">Inativos 60+ dias</SelectItem>
                <SelectItem value="vip">VIP (top 10%)</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="qr_leads">Leads QR de Mesa</SelectItem>
                <SelectItem value="qr_consented">QR Mesa (c/ consentimento)</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Mensagem (ex: Sentimos sua falta! Volte com 10% de desconto)" value={newCampaign.message_template} onChange={e => setNewCampaign(p => ({ ...p, message_template: e.target.value }))} />
            <div className="flex gap-2">
              <Button onClick={handleCreateCampaign} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-campaign-btn">Criar campanha</Button>
              <Button variant="outline" onClick={() => setShowNewCampaign(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {campaigns.length === 0 ? (
          <p className="text-sm text-center text-muted-foreground py-6">Nenhuma campanha criada ainda.</p>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`campaign-${c.id}`}>
                <div className="flex-1 min-w-0 mr-3">
                  <p className="font-medium text-sm">{c.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{c.segment} · {new Date(c.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.status === "error" && (
                    <button onClick={() => handleResendCampaign(c.id)} className="text-[10px] px-2 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50" title="Reenviar">
                      Reenviar
                    </button>
                  )}
                  <div className="text-right">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                      c.status === "sent" ? "bg-emerald-100 text-emerald-700" :
                      c.status === "sending" ? "bg-blue-100 text-blue-700" :
                      c.status === "error" ? "bg-red-100 text-red-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>{c.status === "sending" ? "enviando..." : c.status}</span>
                    <p className="text-[10px] text-muted-foreground mt-1">{c.sent_count} enviados</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </MainLayout>
  );
}
